import { searchAndShowProductImages } from '@frontend/store/creative-studio/chat-function-handlers/search-products';
import { postDesignAndAwait } from '@frontend/store/creative-studio/await-design-job';
import { resolveProductOverlayUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { extractAsin } from '@frontend/store/creative-studio/amazon-asin';

export const handleGenerateDesignConcept = async ($message, { commit, dispatch, state } = {}) => {
  let args = {};

  try {
    args = typeof $message.function_call.arguments === 'string'
      ? JSON.parse($message.function_call.arguments)
      : $message.function_call.arguments || {};
  } catch (e) {
    return;
  }

  const prompts = Array.isArray(args.image_prompts)
    ? args.image_prompts.map(p => p?.trim()).filter(Boolean)
    : [args.image_prompt?.trim()].filter(Boolean);

  const size = args.size;

  if (!prompts.length) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'I could not generate a design idea because the prompt was empty.',
    });
    return;
  }

  const latestUserMessage = [...state.messages].reverse().find(msg => msg.role === 'user');
  const referenceAttachments = args.use_attachments_as_reference
    ? (latestUserMessage?.attachments || [])
    : [];

  const productAttachments = args.product_image_names || [];

  if (productAttachments.length > 0 && commit && dispatch) {
    const productReferences = productAttachments.map(name => {
      // Accept a bare ASIN or a full Amazon URL (extract the ASIN). (POR-13612-2)
      const asinFromName = extractAsin(name);
      let type = 'name';
      let value = name;
      if (asinFromName) { type = 'asin'; value = asinFromName; }
      else if (/^\d{12,14}$/.test(name)) type = 'gtin';
      return { type, value, display_name: value };
    });
    
    await searchAndShowProductImages(productReferences, { commit, dispatch });
  }

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content:
      prompts.length === 1
        ? 'Generating a design concept...'
        : `Generating ${prompts.length} design concepts...`,
  });

  try {
    const productReferencesForResolution = productAttachments.map(name => {
      // Accept a bare ASIN or a full Amazon URL (extract the ASIN). (POR-13612-2)
      const asinFromName = extractAsin(name);
      let type = 'name';
      let value = name;
      if (asinFromName) { type = 'asin'; value = asinFromName; }
      else if (/^\d{12,14}$/.test(name)) type = 'gtin';
      return { type, value, display_name: value };
    });

    const resolvedProductUrl = await resolveProductOverlayUrl(productReferencesForResolution, state.messages);
    const finalProductAttachments = [...productAttachments];
    if (resolvedProductUrl && !finalProductAttachments.includes(resolvedProductUrl)) {
      finalProductAttachments.push(resolvedProductUrl);
    }

    const imageUrls = await Promise.all(
      prompts.map(prompt =>
        generateChatConceptImage({
          prompt,
          size,
          attachments: referenceAttachments,
          productAttachments: finalProductAttachments,
        })
      )
    );

    const fullUrls = imageUrls
      .flat()
      .filter(Boolean)
      .map(url => (url.startsWith('http') ? url : `${window.location.origin}${url}`));

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content:
        fullUrls.length === 1
          ? 'Here is a design concept for you:'
          : `Here are ${fullUrls.length} design concepts for you:`,
      attachments: fullUrls.map(url => ({
        type: 'url',
        payload: url,
      })),
      _designConceptPrompts: prompts,
      widgets: [
        {
          type: 'action_buttons',
          id: 'design_concepts_actions',
          buttons: [
            {
              label: 'Create layout',
              action: 'create_layout_from_design_concept',
              variant: 'primary',
              iconClass: 'bi bi-arrow-right',
            },
          ],
        },
      ],
    });

    return { fullUrls };
  } catch (e) {
    console.error('Failed to generate design concept(s)', e);
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'I could not generate the design images. Please try again.',
    });
  }
};

async function generateChatConceptImage({
  prompt,
  size = { width: 400, height: 300 },
  attachments = [],
  productAttachments = []
}) {
  const formData = new FormData();
  formData.append('image_prompt', prompt);
  formData.append('user_sizes', `${size.width}x${size.height}`);

  for (const att of attachments) {
    const isProductRef = prompt.toLowerCase().includes('using this product') || 
                         prompt.toLowerCase().includes('using that product') ||
                         prompt.toLowerCase().includes('using the attached product');
    
    if (att.type === 'url' && att.payload) {
      if (isProductRef) {
        formData.append('product_images_names[]', att.payload);
      } else {
        formData.append('reference_images_urls[]', att.payload);
      }
    } else if (att.type === 'file' && att.payload instanceof File) {
      if (isProductRef) {
        formData.append('product_images_files[]', att.payload); // Backend doesn't support this yet, but good for completeness
      } else {
        formData.append('reference_images_files[]', att.payload);
      }
    }
  }

  for (const product of productAttachments) {
    formData.append('product_images_names[]', product);
  }

  formData.append('source', 'creative_studio_chat');

  const images = await postDesignAndAwait('/networks/design', formData, true);

  if (Array.isArray(images)) {
    return images.map(item => (typeof item === 'string' ? item : item?.image_url)).filter(Boolean);
  }
  return images ? [images] : [];
}